<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useFormat } from '../composables/useFormat'
import { PRICES, PROVIDER_IDS, pricesUpdatedAt } from '../model/priceBook'

/** Where the numbers come from, how fresh they are, and what they leave out. */
const { t } = useI18n()
const { date } = useFormat()

const NOTES = ['matching', 'windows', 'freeTiers', 'excluded', 'discounts'] as const
</script>

<template>
  <section id="prices" class="panel prices" aria-labelledby="prices-heading">
    <h2 id="prices-heading" class="panel__title">{{ t('prices.heading') }}</h2>
    <p class="prices__updated" data-testid="prices-updated">
      {{ t('prices.updated', { date: date(pricesUpdatedAt(PRICES)) }) }}
    </p>

    <div class="prices__scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">{{ t('prices.provider') }}</th>
            <th scope="col">{{ t('prices.region') }}</th>
            <th scope="col">{{ t('prices.checked') }}</th>
            <th scope="col">{{ t('prices.source') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="id in PROVIDER_IDS" :key="id">
            <th scope="row">{{ PRICES.providers[id].name }}</th>
            <td>
              {{ PRICES.providers[id].regionName }}
              <span class="mono prices__code">{{ PRICES.providers[id].region }}</span>
            </td>
            <td>{{ date(PRICES.providers[id].updatedAt) }}</td>
            <td>
              <a :href="PRICES.providers[id].source" rel="noopener" target="_blank">
                {{ t('prices.sourceLink', { provider: PRICES.providers[id].name }) }}
              </a>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ul class="prices__notes">
      <li v-for="note in NOTES" :key="note">{{ t(`prices.${note}`) }}</li>
    </ul>
  </section>
</template>

<style scoped>
.prices__updated {
  margin: var(--space-2) 0 0;
  color: var(--color-text-muted);
}

.prices__scroll {
  overflow-x: auto;
  margin-top: var(--space-4);
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-sm);
}

th,
td {
  padding: var(--space-2) var(--space-3);
  border-bottom: 1px solid var(--color-border);
  text-align: left;
  white-space: nowrap;
}

thead th {
  color: var(--color-text-muted);
  font-weight: 500;
}

.prices__code {
  margin-left: var(--space-1);
  color: var(--color-text-muted);
}

.prices__notes {
  margin: var(--space-4) 0 0;
  padding-left: var(--space-6);
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.prices__notes li + li {
  margin-top: var(--space-2);
}
</style>
